import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, CheckCircle2, XCircle, ClipboardCheck, Lock,
  Clock,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  getBookingChecklist,
  getBooking,
  seedBookingChecklist,
  signOffChecklistStage,
  updateChecklistStage,
} from "@/lib/api";

const STAGE_LABEL = {
  office_booking: "1 · Office booking",
  gate_check:     "2 · Gate check (FLD)",
  pre_load:       "3 · Pre-load check (FLD)",
  during_load:    "4 · During load (FLD)",
  post_load:      "5 · Post-load sign-off (FLD)",
};

const STAGE_HELP = {
  office_booking: "Booking entered & confirmed by office staff. Vehicle type, registrations, weights, restraint plan, pallet count.",
  gate_check:     "Driver arrives, FLD verifies before bay access: PPE, vehicle clean & tidy, contamination check, pallet type, temp set.",
  pre_load:       "Before loading begins: FLD confirms damage-free pallets & products. Sample temperatures captured if required.",
  during_load:    "During the load/unload: FLD monitors restraint, exclusion zones, line-of-sight rules. Raise NCRs from QA tab if any deviation.",
  post_load:      "After load complete: FLD signs off final time. Kagome rule: product into freezer < 2h from arrival.",
};

const STATUS_STYLE = {
  not_started:   "bg-slate-100 text-slate-600",
  in_progress:   "bg-amber-100 text-amber-700",
  passed:        "bg-emerald-100 text-emerald-700",
  failed:        "bg-rose-100 text-rose-700",
  not_required:  "bg-slate-100 text-slate-400 italic",
};

export default function BookingChecklist() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [booking, setBooking] = useState(null);
  const [stages,  setStages]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId,  setBusyId]  = useState(null);
  const [tick,    setTick]    = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: b } = await getBooking(bookingId);
      if (cancelled) return;
      setBooking(b);

      // Auto-seed if no stages exist yet
      let cl = await getBookingChecklist(bookingId);
      if (!cancelled && (cl.stages || []).length === 0) {
        const seedRes = await seedBookingChecklist(bookingId);
        if (seedRes.error) toast.error(seedRes.error.message);
        cl = await getBookingChecklist(bookingId);
      }
      if (cancelled) return;
      if (cl.error) toast.error(cl.error.message);
      setStages(cl.stages || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [bookingId, tick]);

  const userRole = useMemo(
    () => profile?.role || profile?.roles?.[0] || "unknown",
    [profile]
  );
  const userFullName = useMemo(() => {
    const f = profile?.first_name || "";
    const l = profile?.last_name || "";
    return [f, l].filter(Boolean).join(" ") || user?.email || "Unknown";
  }, [profile, user]);

  const handleSignOff = async (stage, status) => {
    setBusyId(stage.id);
    const { error } = await signOffChecklistStage({
      stageId: stage.id,
      status,                                  // 'passed' | 'failed'
      userId: user?.id,
      role: userRole,
      signatureText: userFullName,
      notes: stage._draftNotes ?? stage.notes,
      data: stage._draftData ?? stage.data,
    });
    setBusyId(null);
    if (error) { toast.error(error.message); return; }
    toast.success(`Stage ${status}.`);
    refresh();
  };

  const handleMarkNotRequired = async (stage) => {
    if (!window.confirm(`Mark "${STAGE_LABEL[stage.stage_key]}" as not required for this booking?`)) return;
    setBusyId(stage.id);
    const { error } = await updateChecklistStage(stage.id, {
      status: "not_required",
      completed_by_user_id: user?.id,
      completed_by_role: userRole,
      signature_text: userFullName,
    });
    setBusyId(null);
    if (error) { toast.error(error.message); return; }
    refresh();
  };

  const setDraft = (stageId, patch) => {
    setStages((arr) => arr.map((s) => (s.id === stageId ? { ...s, ...patch } : s)));
  };

  // Sequential gating: a stage is locked until the previous stage is passed
  // or marked not_required. (failed unblocks downstream too — supervisor
  // can override.)
  const lockMap = useMemo(() => {
    const m = {};
    let prevUnlocked = true;
    for (const s of stages) {
      m[s.id] = !prevUnlocked;
      prevUnlocked = s.status === "passed" || s.status === "not_required" || s.status === "failed";
    }
    return m;
  }, [stages]);

  const progress = useMemo(() => {
    const done = stages.filter((s) => s.status === "passed" || s.status === "not_required").length;
    return { done, total: stages.length };
  }, [stages]);

  if (loading) {
    return <div data-testid="checklist-loading" className="text-sm text-slate-500">Loading checklist…</div>;
  }

  return (
    <div data-testid="booking-checklist-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 -ml-1 mb-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" />
            Intake Form
          </h1>
          <p className="text-sm text-slate-500">
            {booking?.reference_number || bookingId} —
            {booking?.sender_name ? ` ${booking.sender_name} →` : ""} {booking?.receiver_name || ""}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums" data-testid="checklist-progress">
            {progress.done} <span className="text-slate-400 text-base">/ {progress.total}</span>
          </div>
          <div className="text-xs text-slate-500">stages complete</div>
        </div>
      </div>

      <div className="space-y-3">
        {stages.map((stage) => {
          const locked = lockMap[stage.id];
          const done = stage.status === "passed" || stage.status === "not_required";
          const failed = stage.status === "failed";
          return (
            <Card
              key={stage.id}
              data-testid={`stage-${stage.stage_key}`}
              className={done ? "border-emerald-200" : failed ? "border-rose-200" : ""}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {locked && <Lock className="h-4 w-4 text-slate-300" />}
                    {stage.status === "passed" && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                    {stage.status === "failed" && <XCircle className="h-5 w-5 text-rose-500" />}
                    <h2 className="text-base font-semibold">{STAGE_LABEL[stage.stage_key]}</h2>
                    <Badge className={`text-[10px] ${STATUS_STYLE[stage.status]}`}>{stage.status.replace("_", " ")}</Badge>
                  </div>
                  {stage.completed_at && (
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(stage.completed_at).toLocaleString("en-AU", { hourCycle: "h23" })}
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">{STAGE_HELP[stage.stage_key]}</p>

                {!locked && stage.status === "not_started" && (
                  <div className="space-y-2 border-t pt-3">
                    <div>
                      <Label className="text-xs text-slate-500">Sign-off notes</Label>
                      <Textarea
                        rows={2}
                        value={stage._draftNotes ?? stage.notes ?? ""}
                        onChange={(e) => setDraft(stage.id, { _draftNotes: e.target.value })}
                        placeholder="Any observations or conditions worth recording…"
                        data-testid={`stage-notes-${stage.stage_key}`}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                      <div className="text-[11px] text-slate-500">
                        Signing as <span className="font-mono">{userFullName}</span> ·{" "}
                        <span className="font-mono">{userRole}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm" variant="ghost"
                          disabled={busyId === stage.id}
                          onClick={() => handleMarkNotRequired(stage)}
                          data-testid={`stage-skip-${stage.stage_key}`}
                        >
                          Mark not required
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="text-rose-600 border-rose-200 hover:bg-rose-50"
                          disabled={busyId === stage.id}
                          onClick={() => handleSignOff(stage, "failed")}
                          data-testid={`stage-fail-${stage.stage_key}`}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Fail
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === stage.id}
                          onClick={() => handleSignOff(stage, "passed")}
                          data-testid={`stage-pass-${stage.stage_key}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Pass & sign
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {(done || failed) && (
                  <div className="border-t pt-2 text-xs text-slate-600 space-y-1">
                    <div>
                      Signed by <span className="font-mono">{stage.signature_text || "—"}</span>
                      {stage.completed_by_role && ` · ${stage.completed_by_role}`}
                    </div>
                    {stage.notes && <div className="text-slate-500 whitespace-pre-wrap">{stage.notes}</div>}
                    {!locked && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() =>
                          updateChecklistStage(stage.id, { status: "not_started", completed_at: null, signature_text: null })
                            .then(refresh)
                        }
                        data-testid={`stage-reopen-${stage.stage_key}`}
                      >
                        Reopen stage
                      </Button>
                    )}
                  </div>
                )}

                {locked && (
                  <div className="text-xs text-slate-400 border-t pt-2">
                    Locked — complete the previous stage first.
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-xs text-slate-400 text-center pt-2">
        <Link to={`/bookings`} className="hover:underline">Back to bookings</Link>
      </div>
    </div>
  );
}
